/* eslint-disable */

import { checkCollider, CIRCLE } from '@lib/systems/collision'
import { useUpdate } from '@lib/systems/update'
import { useMouseInput, LEFT_BUTTON, RIGHT_BUTTON } from '@lib/systems/mouseInput'
import { useState } from '@lib/systems/state'
import { useUi } from '@lib/systems/ui'
import { useCamera } from '@lib/systems/camera'

import { enableAnimations, useAnimations } from '@game/systems/animations'
import { usePalette } from '@game/systems/palette'
import { translatePomPos, scaleToPom, pomScreenOffset } from '@game/util/translatePomPos'
import { setContext, findPoms, findMoves, movePomTo, clearSelected, nextTurn, endGame } from '@game/util/board'
import { resolveTurn } from '@game/util/computerPlayer'
import { useParallax } from '@game/systems/parallax'
import { useParticles } from '@game/systems/particles'

import {
  LOCAL,
  REMOTE,
  COMPUTER,
  VACANT,
  PLAYER_1,
  PLAYER_2,
  PLAYER_4,
  SOLID,
  HIT_RADIUS
} from '@game/constants'

import {
  networkState,
  sendNetworkMessage,
  C_EXECUTE_TURN,
  C_SELECT_CELL
} from '@game/util/network'

export function executeTurn (i, board) {
  // Do turn
  const res = movePomTo(i)
  clearSelected()

  // Check win conditions
  const otherPlayer = board.whoseTurn === PLAYER_1
    ? PLAYER_2
    : PLAYER_1

  const canMove = !!findMoves(board.whoseTurn, true)
    .reduce((acc, item) => acc + item.jumpTo.length + item.splitTo.length, 0)
  const otherCanMove = !!findMoves(otherPlayer, true)
    .reduce((acc, item) => acc + item.jumpTo.length + item.splitTo.length, 0)
  
  if (!canMove || !otherCanMove) {
    endGame()
  } else {
    nextTurn()
  }

  return res
}

export function selectCell (i, selected, board) {
  board.selected = selected

  if (selected) {
    findPoms(i)
  } else {
    board.canJumpTo = null
    board.canSplitTo = null
  }
}

export default function () {
  const state = useState()
  let hitState = {}
  const hoverState = {}

  const uiContext = useUi()
  const uiState = uiContext.state

  const {
    btnPressed
  } = useMouseInput()

  const {
    actors,
    jiggle,
    clearSelection
  } = useAnimations()

  const {
    currentPalette
  } = usePalette()

  const {
    panCameraTo
  } = useCamera()

  const {
    emit
  } = useParticles()

  let x, y, ax, ay, actor, pom, color

  useParallax(function (render) {
    if (!state.board) return

    const pal = currentPalette()

    // Move selection UI
    color = state.board.whoseTurn === PLAYER_1
      ? pal.accent + 1
      : pal.accent - 1
    for (let i = 0; i < Object.keys(actors).length; i++) {
      if (actors[i].selectPom) {
        ({ x, y } = translatePomPos(actors[i].selectPom))
        render('selection', x, y, 0, actors[i].selectRadius, color, pal.background)
      }
    }

    for (let i = 0; i < state.board.poms.length; i++) {
      if (!actors[i]) return

      actor = actors[i]
      pom = state.board.poms[i]

      ; ({ x, y } = translatePomPos(i))
      ax = x + actor.x
      ay = y + actor.y

      // Draw vacant spots
      if (pom === SOLID || pom === VACANT) {
        if (pom === VACANT) {
          color = pal.background
          render('vacant', x, y, 0, 1, color)
        }
        continue
      }

      // Shadow
      color = (
        (hoverState[i] === true && (
          (state.board.opponentMode === LOCAL) ||
          (state.board.opponentMode === REMOTE && state.board.whoseTurn === networkState.player.which && pom === networkState.player.which) ||
          (state.board.opponentMode === COMPUTER && state.board.whoseTurn === PLAYER_1 && pom === PLAYER_1)
        )) ||
        state.board.selected === i
      )
        ? pal.background + 1
        : pal.background - 1
      render('shadow', x + (actor.x * 0.5), y + (actor.y * 0.5), 0, actor.scale, color, pal.background)

      if (state.board.selected === i) {
        color = pom === PLAYER_1
          ? pal.player1 + 1
          : pal.player2 + 1
      } else {
        color = pom === PLAYER_1
          ? pal.player1
          : pal.player2
      }

      if (pom >= PLAYER_1 && pom <= PLAYER_4) {
        render('stem', x, y, 0, actor.scale, color, null, actor)
        render(pom === PLAYER_1 ? 'pom' : 'shroom', ax, ay, 1, actor.scale, color, pal.background)
      }
    }
  })

  let lastSelected

  useUpdate(function () {
    if (!state.board) return

    const pal = currentPalette()

    setContext(state.board)

    // Win/lose state
    if (state.board.winner !== false) {
      uiState.currentScene = 'versusRematch'
      const loser = state.board.winner === PLAYER_1 ? PLAYER_2 : PLAYER_1
      for (let k in actors) {
        if (state.board.poms[k] === loser) actors[k].tarscale = 0.25
        if (state.board.poms[k] === state.board.winner) actors[k].tarscale = 1.25
      }
      return
    }

    // Opponent moves
    if (!state.board.editBoard) {
      if (state.board.opponentMode === REMOTE && state.board.whoseTurn !== networkState.player.which) {
        return
      }

      if (state.board.opponentMode === COMPUTER && state.board.whoseTurn !== PLAYER_1) {
        resolveTurn(lastSelected).then((r) => {
          r.forEach((k) => {
            actors[k].scale = 0
            actors[k].tarscale = 1
            jiggle(k, 5)
            const pos = translatePomPos(k)
            emit(
              [7, 13],
              pos.x, pos.y,
              [pal.accent, state.board.poms[k] === PLAYER_1 ? pal.player1 : pal.player2],
              {
                velx: (Math.random() * 4) + 2,
                vely: (Math.random() * 4) + 2,
              }
            )
          })
        })
        return
      }
    }

    // Handle player moves
    const res = {}
    for (let i = 0; i < state.board.poms.length; i++) {
      res[i] = (
        checkCollider({
          position: translatePomPos(i),
          shape: CIRCLE,
          radius: scaleToPom(HIT_RADIUS)
        }, 'cursor') ||
        checkCollider({
          position: {
            x: translatePomPos(i).x,
            y: translatePomPos(i).y - (HIT_RADIUS / 2)
          },
          shape: CIRCLE,
          radius: scaleToPom(HIT_RADIUS)
        }, 'cursor')
      )

      if (
        (
          (state.board.opponentMode === LOCAL) ||
          (state.board.opponentMode === REMOTE && state.board.whoseTurn === networkState.player.which) ||
          (state.board.opponentMode === COMPUTER && state.board.whoseTurn === PLAYER_1)
        ) &&
        res[i]
      ) {
        // BOARD EDITOR
        if (state.board.editBoard) {
          if (btnPressed(LEFT_BUTTON)) {
            if (state.board.poms[i] === VACANT) state.board.poms[i] = SOLID
            else state.board.poms[i] = VACANT
            const board = JSON.stringify(state.board.poms)
            navigator.clipboard.writeText(board)
          } else if (btnPressed(RIGHT_BUTTON)) {
            state.board.poms[i] = (state.board.poms[i] + 1) % 3
            const board = JSON.stringify(state.board.poms)
            navigator.clipboard.writeText(board)
          }
          continue
        }
        // END BOARD EDITOR

        hitState[i] = true

        if (hoverState[i] !== true) {
          if (state.board.poms[i] !== VACANT && state.board.poms[i] !== SOLID) {
            hoverState[i] = true
            const pos = translatePomPos(i)
            emit(Math.random() > 0.5 ? [0, 2] : 0, pos.x, pos.y, [pal.accent, state.board.poms[i] === PLAYER_1 ? pal.player1 : pal.player2])
            jiggle(i, 1)
          }
        }

        if (!btnPressed(LEFT_BUTTON)) continue
        if (state.board.opponentMode === REMOTE && state.board.whoseTurn !== networkState.player.which) continue
        if (state.board.opponentMode === COMPUTER && state.board.whoseTurn !== PLAYER_1) continue

        // split/jump to cell
        if (
          state.board.selected &&
          (state.board.splitHash[i] || state.board.jumpHash[i])
        ) {
          hitState = {}
          lastSelected = i
          actors[i].scale = 0
          actors[i].tarscale = 1

          let pos = translatePomPos(i)
          emit(
            [7, 13],
            pos.x, pos.y,
            [pal.accent, state.board.whoseTurn === PLAYER_1 ? pal.player1 : pal.player2],
            {
              velx: (Math.random() * 4) + 2,
              vely: (Math.random() * 4) + 2,
            }
          )
          jiggle(i, 5)
          clearSelection()
          panCameraTo(pomScreenOffset(i))
          executeTurn(i, state.board).forEach((k) => {
            actors[k].scale = 0
            actors[k].tarscale = 1
            pos = translatePomPos(k)
            emit(
              [7, 13],
              pos.x, pos.y,
              [pal.accent, state.board.whoseTurn === PLAYER_1 ? pal.player1 : pal.player2],
              {
                velx: (Math.random() * 4) + 2,
                vely: (Math.random() * 4) + 2,
              }
            )
            jiggle(k, 5)
          })
          if (state.board.opponentMode === REMOTE) {
            sendNetworkMessage(C_EXECUTE_TURN, { i })
          }
        }

        // select cell
        else if (
          (state.board.opponentMode === LOCAL && state.board.poms[i] === state.board.whoseTurn) ||
          (state.board.opponentMode === REMOTE && state.board.poms[i] === networkState.player.which) ||
          (state.board.opponentMode === COMPUTER && state.board.poms[i] === PLAYER_1)
        ) {
          const selected = state.board.selected === i ? false : i

          if (selected !== false) {
            let pos = translatePomPos(i)
            emit(
              [3, 5],
              pos.x, pos.y,
              [pal.accent, state.board.poms[i] === PLAYER_1 ? pal.player1 : pal.player2],
              {
                velx: (Math.random() * 2) + 1,
                vely: (Math.random() * 2) + 1,
              }
            )
            jiggle(i, 3)
          } else {
            hitState = {}
          }

          selectCell(i, selected, state.board)
          if (selected !== false) panCameraTo(pomScreenOffset(i))
          if (state.board.opponentMode === REMOTE) {
            sendNetworkMessage(C_SELECT_CELL, { i })
          }
        }
      }

      if (!res[i]) {
        delete hoverState[i]
      }
    }

    hitState = {}
  })

  enableAnimations()
}
